import React from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentRequestButtonElement } from "@stripe/react-stripe-js";

// Cache the payment request and initial wallet check
let paymentRequestCache = null;
let stripeInstance = null;
let initialWalletsChecked = false;
let availableWallets = [];

export const GooglePayButton = ({ onClick }) => {
	return (
		<button
			onClick={onClick}
			type="button"
			className="next-button btn btn--solid btn-googlepay digital-wallet-button"
		>
			<span>Donate with</span>
			<GooglePaySVG />
			<span>Pay</span>
		</button>
	);
};

export const ApplePayButton = ({ onClick }) => {
	return (
		<button
			onClick={onClick}
			type="button"
			className="next-button btn btn--solid btn-applepay digital-wallet-button"
		>
			<span>Donate with</span>
			<ApplePaySVG />
			<span>Pay</span>
		</button>
	);
};

export const PaymentMethodLabel = ({ type }) => {
	switch (type) {
		case "googlepay":
			return (
				<span className="digital-wallet-label d-flex align-items-center justify-content-center gap-1">
					<GooglePaySVG /> Pay
				</span>
			);
		case "applepay":
			return (
				<span className="digital-wallet-label d-flex align-items-center justify-content-center gap-1">
					<ApplePaySVG /> Pay
				</span>
			);
		case "paypal":
			return (
				<span className="digital-wallet-label d-flex align-items-center justify-content-center gap-1">
					<PayPalSVG /> PayPal
				</span>
			);
		default:
			return (
				<span className="digital-wallet-label d-flex align-items-center justify-content-center gap-1">
					<WalletSVG /> Pay
				</span>
			);
	}
};

export const DigitalWalletSVG = ({ type }) => {
	switch (type) {
		case "googlepay":
			return <GooglePaySVG />;
		case "applepay":
			return <ApplePaySVG />;
		default:
			return <WalletSVG />;
	}
};

export const GooglePaySVG = () => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		height="24"
		viewBox="0 0 24 24"
		width="24"
	>
		<path
			d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
			fill="#4285F4"
		/>
		<path
			d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
			fill="#34A853"
		/>
		<path
			d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
			fill="#FBBC05"
		/>
		<path
			d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
			fill="#EA4335"
		/>
		<path d="M1 1h22v22H1z" fill="none" />
	</svg>
);

export const ApplePaySVG = () => (
	<svg width="20" height="16" viewBox="0 0 20 16" fill="none">
		<path
			d="M8.1 2.4c.4-.5.7-1.2.6-1.9-.6 0-1.3.4-1.7.9-.4.4-.7 1.1-.6 1.7.7.1 1.3-.3 1.7-.7zm.6 1.1c-1-.1-1.8.6-2.3.6s-1.2-.6-2-.6c-1 0-1.9.6-2.4 1.5-1 1.7-.3 4.3.7 5.7.5.7 1.1 1.4 1.9 1.4.8 0 1.1-.5 2.1-.5s1.2.5 2.1.5c.9 0 1.3-.7 1.8-1.4.6-.8.8-1.5.8-1.6 0 0-1.6-.6-1.6-2.4 0-1.5 1.2-2.2 1.3-2.3-.7-1-1.8-1.1-2.2-1.1-.1 0-.2.1-.2.2z"
			fill="#000"
		/>
	</svg>
);

export const WalletSVG = () => (
	<svg width="20" height="16" viewBox="0 0 20 16" fill="none">
		<path
			d="M18 4H2a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2Z"
			fill="currentColor"
		/>
		<path
			d="M18 2H2a2 2 0 0 0-2 2v.5a2.5 2.5 0 0 1 2-1.5h16a2.5 2.5 0 0 1 2 1.5V4a2 2 0 0 0-2-2Z"
			fill="currentColor"
			opacity="0.6"
		/>
		<circle cx="15" cy="9" r="1.5" fill="white" />
	</svg>
);

export const PayPalSVG = () => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		xmlnsXlink="http://www.w3.org/1999/xlink"
		fill="none"
		viewBox="0 0 48 48"
		width="20px"
		height="20px"
	>
		<path
			fill="#001C64"
			d="M37.972 13.82c.107-5.565-4.485-9.837-10.799-9.837H14.115a1.278 1.278 0 0 0-1.262 1.079L7.62 37.758a1.038 1.038 0 0 0 1.025 1.2h7.737l-1.21 7.572a1.038 1.038 0 0 0 1.026 1.2H22.5c.305 0 .576-.11.807-.307.231-.198.269-.471.316-.772l1.85-10.885c.047-.3.2-.69.432-.888.231-.198.433-.306.737-.307H30.5c6.183 0 11.43-4.394 12.389-10.507.678-4.34-1.182-8.287-4.916-10.244Z"
		></path>
		<path
			fill="#0070E0"
			d="m18.056 26.9-1.927 12.22-1.21 7.664a1.038 1.038 0 0 0 1.026 1.2h6.67a1.278 1.278 0 0 0 1.261-1.079l1.758-11.14a1.277 1.277 0 0 1 1.261-1.078h3.927c6.183 0 11.429-4.51 12.388-10.623.68-4.339-1.504-8.286-5.238-10.244-.01.462-.05.923-.121 1.38-.959 6.112-6.206 10.623-12.389 10.623h-6.145a1.277 1.277 0 0 0-1.261 1.077Z"
		></path>
		<path
			fill="#003087"
			d="M16.128 39.12h-7.76a1.037 1.037 0 0 1-1.025-1.2l5.232-33.182a1.277 1.277 0 0 1 1.262-1.078h13.337c6.313 0 10.905 4.595 10.798 10.16-1.571-.824-3.417-1.295-5.44-1.295H21.413a1.278 1.278 0 0 0-1.261 1.078L18.057 26.9l-1.93 12.22Z"
		></path>
	</svg>
);

export const getDigitalWallets = async (stripePublicKey, currentAmount) => {
	if (!currentAmount) return [];

	try {
		// Initialize Stripe instance if not already done
		if (!stripeInstance) {
			stripeInstance = await loadStripe(stripePublicKey);
		}

		console.log("stripeInstance", stripeInstance);
		console.log("currentAmount", currentAmount);
		console.log("stripePublicKey", stripePublicKey);

		// Create payment request if not cached or if we need to reinitialize
		if (!paymentRequestCache) {
			paymentRequestCache = stripeInstance.paymentRequest({
				country: "GB",
				currency: "gbp",
				total: {
					label: "Donation",
					amount: currentAmount * 100,
				},
				requestPayerName: true,
				requestPayerEmail: true,
				requestShipping: false,
				requestPayerPhone: false,
			});
			console.log("paymentRequestCache", paymentRequestCache);

			// Only check canMakePayment once during initialization
			if (!initialWalletsChecked) {
				const result = await paymentRequestCache.canMakePayment();
				console.log("result", result);
				if (!result) {
					availableWallets = [];
				} else {
					availableWallets = Object.keys(result)
						.filter((key) => result[key])
						.map((walletType) => ({
							type: walletType.toLowerCase(),
							pr: paymentRequestCache,
							label: walletType.charAt(0).toUpperCase() + walletType.slice(1),
						}));
				}

				// Simulate Google Pay in dev environments
				const isDev =
					typeof window !== "undefined" &&
					(window.location.href.includes("localhost") ||
						window.location.href.includes(".build"));
				if (isDev) {
					const hasGooglePay = availableWallets.some(
						(wallet) => wallet.type === "googlepay"
					);
					if (!hasGooglePay) {
						availableWallets.push({
							type: "googlepay",
							pr: paymentRequestCache,
							label: "GooglePay",
							isSimulated: true, // Flag to indicate this is simulated for dev
						});
					}
				}

				initialWalletsChecked = true;
			}
		} else {
			// Use update() to change the amount instead of recreating
			paymentRequestCache.update({
				total: {
					label: "Donation",
					amount: currentAmount * 100,
				},
			});
		}
		console.log("availableWallets", availableWallets);

		return availableWallets;
	} catch (error) {
		console.error("Error checking wallet availability:", error);
		return [];
	}
};
